import type { AnalysisSpec, SemanticFilter, VisualPoint } from '@lognai/plugin-analytics';

export interface DrillCrumb {
  member: string;
  label: string;
}

const timePart = (member: string): 'day' | 'week' | 'month' | null => {
  const match = member.match(/\.(day|week|month)$/);
  return match ? match[1] as 'day' | 'week' | 'month' : null;
};

const titleCase = (value: string): string =>
  value.replace(/_/g, ' ').replace(/\b\w/g, (character) => character.toUpperCase());

export const formatDrillCrumb = (crumb: DrillCrumb): string => {
  const granularity = timePart(crumb.member);
  if (!granularity) return titleCase(crumb.label);
  const normalized = /T/.test(crumb.label) && !/(?:Z|[+-]\d{2}:?\d{2})$/.test(crumb.label)
    ? `${crumb.label}Z`
    : crumb.label;
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return crumb.label;
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    ...(granularity === 'month' ? { year: 'numeric' as const } : { day: 'numeric' as const }),
    timeZone: 'UTC',
  }).format(date);
};

export const drillMemberLabel = (member: string): string =>
  titleCase(member.replace(/\.(day|week|month)$/, '.$1').split('.').pop() ?? member);

export const drillSubtitle = (crumbs: DrillCrumb[], currentMember?: string): string => {
  if (!crumbs.length) return 'Click a point to drill into the next business dimension.';
  const path = crumbs.map(formatDrillCrumb);
  if (currentMember) path.push(drillMemberLabel(currentMember));
  return `Drilldown: ${path.join(' → ')}`;
};

const dateRangeForPoint = (value: string, granularity: 'day' | 'week' | 'month'): [string, string] => {
  const normalized = /^\d{4}-\d{2}-\d{2}T/.test(value) && !/(?:Z|[+-]\d{2}:?\d{2})$/.test(value)
    ? `${value}Z`
    : value;
  const start = new Date(normalized);
  if (Number.isNaN(start.getTime())) return [value, value];
  const end = new Date(start);
  if (granularity === 'month') end.setUTCMonth(end.getUTCMonth() + 1);
  else if (granularity === 'week') end.setUTCDate(end.getUTCDate() + 7);
  else end.setUTCDate(end.getUTCDate() + 1);
  end.setUTCMilliseconds(end.getUTCMilliseconds() - 1);
  return [start.toISOString().slice(0, 10), end.toISOString().slice(0, 10)];
};

export const semanticFilterForPoint = (
  categoryMember: string,
  point: VisualPoint
): SemanticFilter => {
  const granularity = timePart(categoryMember);
  return granularity
    ? {
        member: categoryMember.replace(/\.(day|week|month)$/, ''),
        operator: 'inDateRange',
        values: dateRangeForPoint(point.key, granularity),
      }
    : { member: categoryMember, operator: 'equals', values: [point.key] };
};

export const drillAnalysis = (
  analysis: AnalysisSpec,
  categoryMember: string,
  point: VisualPoint
): AnalysisSpec | null => {
  const currentIndex = analysis.drillPath.indexOf(categoryMember);
  const next = analysis.drillPath[currentIndex + 1];
  if (!next) return null;

  const filter = semanticFilterForPoint(categoryMember, point);
  const nextGranularity = timePart(next);

  return {
    ...analysis,
    title: analysis.title,
    filters: [...analysis.filters.filter((item) => item.member !== filter.member), filter],
    dimensions: nextGranularity ? [] : [next],
    timeDimension: nextGranularity
      ? {
          member: next.replace(/\.(day|week|month)$/, ''),
          granularity: nextGranularity,
          dateRange: filter.values as [string, string],
        }
      : undefined,
    order: [{ member: analysis.measures[0], direction: 'desc' }],
    limit: next.endsWith('order_number') || next.endsWith('sales_order_id') ? 50 : 15,
  };
};
